import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FilterPanel from '../../components/ui/FilterPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import InfoTooltip from '../../components/ui/InfoTooltip';
import TagMultiSelect from '../../components/crm/TagMultiSelect';
import { createCampaign } from '../../api/campaigns';
import { fetchProspectCountByTags } from '../../api/prospects';

const STEP_IDS = ['info', 'audience'];

const STEP_LABELS = {
  info: { title: 'Informations', short: 'Infos' },
  audience: { title: 'Prospects cibles', short: 'Prospects' },
};

export default function CampaignCreatePage() {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tagNames, setTagNames] = useState([]);
  const [prospectCount, setProspectCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const currentStepId = STEP_IDS[stepIndex] || 'info';
  const isLastStep = stepIndex >= STEP_IDS.length - 1;

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (currentStepId !== 'audience') return;
      if (tagNames.length === 0) {
        if (!cancelled) {
          setProspectCount(0);
          setCountLoading(false);
        }
        return;
      }
      setCountLoading(true);
      try {
        const res = await fetchProspectCountByTags(tagNames);
        if (!cancelled) setProspectCount(res?.data?.count ?? 0);
      } catch {
        if (!cancelled) setProspectCount(null);
      } finally {
        if (!cancelled) setCountLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [tagNames, currentStepId]);

  const canGoNext = useMemo(() => {
    if (currentStepId === 'info') return name.trim().length > 0;
    return true;
  }, [currentStepId, name]);

  const handleFinish = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await createCampaign({
        name: name.trim(),
        description: description.trim(),
        tags: tagNames,
        products: [],
        status: 'active',
      });
      const c = res?.data?.campaign;
      const id = c?._id || c?.id;
      if (!id) throw new Error('Création impossible.');
      navigate(`/campaigns/${encodeURIComponent(id)}/products`, { replace: true });
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Impossible de créer la campagne.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Nouvelle campagne - CRM</title>
      </Helmet>
      <Header />
      <Breadcrumb />
      <FilterPanel />

      <main className="main-content max-w-2xl mx-auto pb-16">
        <div className="mb-6">
          <button
            type="button"
            onClick={() => navigate('/campaigns')}
            className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="ArrowLeft" size={16} />
            Retour aux campagnes
          </button>
          <h1 className="text-2xl md:text-3xl font-semibold text-foreground">Nouvelle campagne</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Étape {stepIndex + 1} sur {STEP_IDS.length} — {STEP_LABELS[currentStepId]?.title || ''}
          </p>
        </div>

        <nav className="mb-8 flex items-center gap-1 sm:gap-2" aria-label="Progression">
          {STEP_IDS.map((id, i) => {
            const done = stepIndex > i;
            const active = stepIndex === i;
            const label = STEP_LABELS[id];
            return (
              <React.Fragment key={id}>
                {i > 0 ? (
                  <div
                    className={`hidden sm:block h-0.5 flex-1 max-w-[3rem] rounded ${
                      done ? 'bg-primary' : 'bg-border'
                    }`}
                    aria-hidden
                  />
                ) : null}
                <div
                  className={`flex items-center gap-2 rounded-full px-2 py-1 sm:px-3 ${
                    active ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
                  }`}
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                      done
                        ? 'bg-primary text-primary-foreground'
                        : active
                          ? 'border-2 border-primary text-primary'
                          : 'border border-border bg-background'
                    }`}
                  >
                    {done ? <Icon name="Check" size={14} /> : i + 1}
                  </span>
                  <span className="hidden sm:inline text-xs font-medium truncate">
                    {label?.short}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
        </nav>

        <div className="rounded-xl border border-border bg-card shadow-elevation-1 p-5 md:p-8">
          {currentStepId === 'info' ? (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">
                Nom et description de la campagne. Produits, HubUP et emails se configurent ensuite
                dans la fiche.
              </p>
              <div>
                <label className="flex items-center gap-1 text-sm font-medium text-foreground mb-1.5">
                  Titre
                  <span className="text-error">*</span>
                  <InfoTooltip text="Un nom court pour retrouver la campagne dans la liste." />
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Ex. Lancement printemps"
                  autoFocus
                />
              </div>
              <div>
                <label className="flex items-center gap-1 text-sm font-medium text-foreground mb-1.5">
                  Description
                  <InfoTooltip text="Optionnel — note interne pour votre équipe." />
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full resize-none rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Objectif, période, remarques…"
                />
              </div>
            </div>
          ) : null}

          {currentStepId === 'audience' ? (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">
                Les tags définissent quels prospects appartiennent à cette campagne : un prospect
                doit avoir <strong className="font-medium text-foreground">tous</strong> les tags
                choisis. Les tags inexistants sont créés à l&apos;enregistrement.
              </p>
              <TagMultiSelect
                label="Tags d'audience"
                value={tagNames}
                onChange={setTagNames}
                placeholder="Rechercher ou créer un tag…"
              />

              <div
                className={`rounded-lg border p-4 ${
                  tagNames.length === 0
                    ? 'border-border bg-muted/30'
                    : 'border-primary/30 bg-primary/5'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Icon name="Users" size={22} className="text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Prospects ciblés par cette campagne
                    </p>
                    {tagNames.length === 0 ? (
                      <p className="text-sm text-muted-foreground mt-1">
                        Ajoutez au moins un tag pour estimer l&apos;audience.
                      </p>
                    ) : countLoading ? (
                      <p className="text-sm text-muted-foreground mt-1">Calcul en cours…</p>
                    ) : prospectCount === null ? (
                      <p className="text-sm text-muted-foreground mt-1">
                        Impossible d&apos;afficher le nombre pour le moment.
                      </p>
                    ) : (
                      <p className="text-2xl font-semibold text-primary mt-1 tabular-nums">
                        {prospectCount}{' '}
                        <span className="text-base font-normal text-muted-foreground">
                          prospect{prospectCount !== 1 ? 's' : ''}
                        </span>
                      </p>
                    )}
                    {tagNames.length > 0 ? (
                      <p className="text-xs text-muted-foreground mt-2">
                        Tags : {tagNames.join(' · ')}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {error ? (
            <p className="mt-4 text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between sm:items-center pt-6 border-t border-border">
            <div>
              {stepIndex > 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStepIndex((i) => i - 1)}
                  disabled={saving}
                >
                  Précédent
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/campaigns')}
                  disabled={saving}
                >
                  Annuler
                </Button>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              {!isLastStep ? (
                <Button
                  type="button"
                  onClick={() => setStepIndex((i) => i + 1)}
                  disabled={!canGoNext}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Continuer
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleFinish}
                  disabled={saving || !name.trim()}
                  iconName="Check"
                  iconPosition="left"
                >
                  {saving ? 'Création…' : 'Créer la campagne'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
